import { useMemo, useState } from "react"
import { APIError, client } from "@/api"
import { Button } from "@/components/Button"
import { Checkbox } from "@/components/Form/Checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/Dialog/Dialog"
import { useToast } from "@/components/Toast"
import {
  buildImportRows,
  ColumnMapping,
  ImportField,
  ImportRole,
  inferColumnMapping,
  parseDelimitedRows,
  parseRosterFile,
} from "@/lib/teamMemberImport"

type ImportOptions = {
  defaultRole: ImportRole
  memberImportMode: "invite" | "assign"
  sendEmails: boolean
  createRosterEntries: boolean
  maxInvitationsToCreate: number | null
  allowReinvite: boolean
}

type PreviewRow = {
  sourceIndex: number
  firstName: string
  lastName: string
  fullName: string
  email: string
  role: ImportRole
  jerseyNumber: string
  position: string | null
  gmailHint: boolean
  member: {
    action: string
    label: string
    processable: boolean
    userPublicId: string | null
  }
  roster: {
    action: string
    label: string
    processable: boolean
    warning: string | null
    playerPublicId: string | null
  }
  warnings: string[]
  result?: {
    member: "added" | "invited" | "skipped" | "failed"
    roster: "created" | "skipped" | "failed"
    emailStatus: "not_requested" | "sent" | "skipped" | "failed"
    error: string | null
  }
}

type ImportPreview = {
  rows: PreviewRow[]
  summary: {
    totalRows: number
    invalidEmails: number
    duplicateUploadEmails: number
    alreadyMembers: number
    pendingInvites: number
    priorInvites: number
    inviteLimitSkipped: number
    existingUsersToAdd: number
    newUsersToCreate: number
    usersToAssign: number
    invitationsToCreate: number
    newUsersToInvite: number
    rosterEntriesToCreate: number
    rosterDuplicateJerseys: number
    rosterExistingPlayers: number
    processableRows: number
  }
  limit: {
    currentMembers: number
    projectedMembersOrInvites: number
    maxTeamMembers: number | null
    exceedsLimit: boolean
  }
}

type ImportResult = ImportPreview & {
  processed: {
    addedMembers: number
    usersCreated: number
    invitationsCreated: number
    rosterEntriesCreated: number
    emailsSent: number
    emailsFailed: number
    skippedRows: number
  }
}

const importFields: Array<{ value: ImportField; label: string }> = [
  { value: "ignore", label: "Ignore" },
  { value: "firstName", label: "First name" },
  { value: "lastName", label: "Last name" },
  { value: "name", label: "Full name" },
  { value: "position", label: "Position" },
  { value: "jerseyNumber", label: "Jersey" },
  { value: "email", label: "Email" },
  { value: "role", label: "Role" },
]

const roleOptions: Array<{ value: ImportRole; label: string }> = [
  { value: "player", label: "Player" },
  { value: "coach", label: "Coach" },
  { value: "parent", label: "Parent" },
  { value: "admin", label: "Admin" },
]

function statusClass(processable: boolean, action: string) {
  if (processable) return "bg-green-100 text-green-800"
  if (action.includes("invalid") || action.includes("duplicate")) return "bg-red-100 text-red-800"
  return "bg-gray-100 text-gray-700"
}

function SummaryCard({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded-md border border-border bg-surface/40 px-3 py-2">
      <div className="text-xs text-secondary">{label}</div>
      <div className="text-lg font-semibold text-primary">{value}</div>
    </div>
  )
}

function importResultDescription(result: ImportResult) {
  const parts = [
    `${result.processed.addedMembers} members assigned`,
    `${result.processed.invitationsCreated} pending invitations created`,
    `${result.processed.rosterEntriesCreated} roster rows created`,
  ]

  if (result.processed.usersCreated > 0) {
    parts.push(`${result.processed.usersCreated} user records created`)
  }
  if (result.processed.emailsSent > 0 || result.processed.emailsFailed > 0) {
    parts.push(`${result.processed.emailsSent} emails sent`)
  }
  if (result.processed.emailsFailed > 0) {
    parts.push(`${result.processed.emailsFailed} email errors`)
  }

  return `${parts.join(", ")}.`
}

export function TeamMemberImportDialog({
  open,
  onOpenChange,
  teamId,
  onComplete,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  teamId: string
  onComplete: () => void
}) {
  const { toast } = useToast()
  const [rows, setRows] = useState<string[][]>([])
  const [mapping, setMapping] = useState<ColumnMapping>({})
  const [hasHeader, setHasHeader] = useState(false)
  const [pasteText, setPasteText] = useState("")
  const [fileName, setFileName] = useState("")
  const [preview, setPreview] = useState<ImportPreview | null>(null)
  const [result, setResult] = useState<ImportResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isPreviewing, setIsPreviewing] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [fileInputKey, setFileInputKey] = useState(0)
  const [options, setOptions] = useState<ImportOptions>({
    defaultRole: "player",
    memberImportMode: "invite",
    sendEmails: true,
    createRosterEntries: false,
    maxInvitationsToCreate: null,
    allowReinvite: false,
  })

  const importRows = useMemo(
    () => buildImportRows(rows, mapping, hasHeader, options.defaultRole),
    [rows, mapping, hasHeader, options.defaultRole]
  )
  const maxColumns = rows[0]?.length ?? 0
  const visibleRows = (result?.rows ?? preview?.rows ?? []).slice(0, 80)
  const limitExceeded = Boolean(preview?.limit.exceedsLimit)
  const hasImportData = rows.length > 0 || pasteText.trim().length > 0 || Boolean(fileName)
  const inviteLimitSkipped = preview?.summary.inviteLimitSkipped ?? 0
  const priorInvites = preview?.summary.priorInvites ?? 0

  const resetResults = () => {
    setPreview(null)
    setResult(null)
    setError(null)
  }

  const applyRows = (nextRows: string[][], nextFileName = "") => {
    const inferred = inferColumnMapping(nextRows)
    setRows(nextRows)
    setMapping(inferred.mapping)
    setHasHeader(inferred.hasHeader)
    setFileName(nextFileName)
    resetResults()
  }

  const clearImport = () => {
    setRows([])
    setMapping({})
    setHasHeader(false)
    setPasteText("")
    setFileName("")
    setFileInputKey((key) => key + 1)
    resetResults()
  }

  const handleFile = async (file: File | null) => {
    if (!file) return
    try {
      applyRows(await parseRosterFile(file), file.name)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Failed to parse roster file")
    }
  }

  const handlePreview = async () => {
    setIsPreviewing(true)
    setError(null)
    setResult(null)
    try {
      const response = await (
        (client.admin.teams[":publicId"] as any)["member-imports"].preview as any
      ).$post({
        param: { publicId: teamId },
        json: { rows: importRows, options },
      })
      setPreview((await response.json()) as ImportPreview)
    } catch (caught) {
      setError(
        caught instanceof APIError
          ? caught.originalError.message
          : caught instanceof Error
            ? caught.message
            : "Failed to preview import"
      )
    } finally {
      setIsPreviewing(false)
    }
  }

  const handleProcess = async () => {
    if (limitExceeded) {
      setError("Import exceeds this team's member limit. Adjust the limit or remove rows first.")
      return
    }

    setIsProcessing(true)
    setError(null)
    try {
      const response = await (
        (client.admin.teams[":publicId"] as any)["member-imports"] as any
      ).$post({
        param: { publicId: teamId },
        json: { rows: importRows, options },
      })
      const data = (await response.json()) as ImportResult
      setResult(data)
      setPreview(data)
      onComplete()
      toast({
        title: "Import processed",
        description: importResultDescription(data),
      })
    } catch (caught) {
      setError(
        caught instanceof APIError
          ? caught.originalError.message
          : caught instanceof Error
            ? caught.message
            : "Failed to process import"
      )
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-7xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Bulk Import Team Members</DialogTitle>
          <DialogDescription>
            Upload a roster file or paste rows, preview changes, then process the import.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_300px]">
          <div className="space-y-4">
            <div className="rounded-md border border-border p-4">
              <div className="grid gap-3 md:grid-cols-2">
                <label className="block">
                  <span className="mb-1 block text-sm font-medium">Roster file</span>
                  <input
                    key={fileInputKey}
                    type="file"
                    accept=".csv,.txt,.tsv,.xlsx"
                    className="block w-full text-sm"
                    onChange={(event) => handleFile(event.target.files?.[0] ?? null)}
                  />
                  {fileName && (
                    <span className="mt-1 block text-xs text-secondary">{fileName}</span>
                  )}
                </label>
                <div>
                  <span className="mb-1 block text-sm font-medium">Paste rows</span>
                  <div className="flex gap-2">
                    <textarea
                      className="min-h-20 flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={pasteText}
                      onChange={(event) => setPasteText(event.target.value)}
                      placeholder="First, Last, Position, Jersey, Email"
                    />
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => applyRows(parseDelimitedRows(pasteText), "Pasted rows")}
                      disabled={!pasteText.trim()}
                    >
                      Load
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {rows.length > 0 && (
              <div className="rounded-md border border-border p-4">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-semibold">Column Mapping</h3>
                    <p className="text-xs text-secondary">{importRows.length} data rows detected</p>
                  </div>
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={hasHeader}
                      onCheckedChange={(checked) => {
                        setHasHeader(Boolean(checked))
                        resetResults()
                      }}
                    />
                    First row is header
                  </label>
                </div>

                <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                  {Array.from({ length: maxColumns }, (_, index) => (
                    <label key={index} className="rounded-md bg-surface/50 p-2 text-sm">
                      <span className="mb-1 block truncate text-xs text-secondary">
                        Column {index + 1}: {rows[0]?.[index] || "Blank"}
                      </span>
                      <select
                        className="w-full rounded-md border border-input bg-background px-2 py-1"
                        value={mapping[index] ?? "ignore"}
                        onChange={(event) => {
                          setMapping({ ...mapping, [index]: event.target.value as ImportField })
                          resetResults()
                        }}
                      >
                        {importFields.map((field) => (
                          <option key={field.value} value={field.value}>
                            {field.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {(preview || result) && (
              <div className="rounded-md border border-border p-4">
                <div className="mb-3 grid grid-cols-2 gap-2 md:grid-cols-4">
                  <SummaryCard label="Rows" value={preview?.summary.totalRows ?? 0} />
                  <SummaryCard
                    label="Assign members"
                    value={preview?.summary.usersToAssign ?? 0}
                  />
                  <SummaryCard
                    label="Create invites"
                    value={preview?.summary.invitationsToCreate ?? 0}
                  />
                  <SummaryCard
                    label="Roster rows"
                    value={preview?.summary.rosterEntriesToCreate ?? 0}
                  />
                </div>

                {preview?.limit.exceedsLimit && (
                  <div className="mb-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                    Projected members/invites exceed this team&apos;s member limit (
                    {preview.limit.projectedMembersOrInvites}/{preview.limit.maxTeamMembers}). Adjust
                    the limit or remove rows before processing.
                  </div>
                )}

                {inviteLimitSkipped > 0 && (
                  <div className="mb-3 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-950">
                    Invite limit is on. {inviteLimitSkipped} eligible rows will wait for a later
                    import run.
                  </div>
                )}

                {priorInvites > 0 && (
                  <div className="mb-3 rounded-md border border-yellow-300 bg-yellow-50 px-3 py-2 text-sm text-yellow-900">
                    {priorInvites} rows already have a prior non-pending invite and will not be
                    invited again unless re-invite is enabled.
                  </div>
                )}

                {result && (
                  <div className="mb-3 grid grid-cols-2 gap-2 md:grid-cols-5">
                    <SummaryCard label="Assigned" value={result.processed.addedMembers} />
                    <SummaryCard label="Invites" value={result.processed.invitationsCreated} />
                    <SummaryCard label="Roster rows" value={result.processed.rosterEntriesCreated} />
                    <SummaryCard label="Emails sent" value={result.processed.emailsSent} />
                    <SummaryCard label="Email errors" value={result.processed.emailsFailed} />
                  </div>
                )}

                <div className="max-h-80 overflow-auto rounded-md border border-border">
                  <table className="w-full min-w-[780px] text-left text-sm">
                    <thead className="sticky top-0 bg-background">
                      <tr className="border-b border-border">
                        <th className="px-3 py-2">Row</th>
                        <th className="px-3 py-2">Name</th>
                        <th className="px-3 py-2">Email</th>
                        <th className="px-3 py-2">Role</th>
                        <th className="px-3 py-2">Jersey</th>
                        <th className="px-3 py-2">Member</th>
                        <th className="px-3 py-2">Roster</th>
                        {result && <th className="px-3 py-2">Result</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {visibleRows.map((row) => (
                        <tr
                          key={`${row.sourceIndex}-${row.email}-${row.jerseyNumber}`}
                          className="border-b border-border"
                        >
                          <td className="px-3 py-2">{row.sourceIndex}</td>
                          <td className="px-3 py-2">{row.fullName || "—"}</td>
                          <td className="px-3 py-2">
                            <div>{row.email || "—"}</div>
                            {row.gmailHint && (
                              <div className="text-xs text-secondary">Google sign-in likely</div>
                            )}
                          </td>
                          <td className="px-3 py-2">{row.role}</td>
                          <td className="px-3 py-2">
                            {row.jerseyNumber || "—"}
                            {row.position && (
                              <span className="text-secondary"> · {row.position}</span>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <span
                              className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(
                                row.member.processable,
                                row.member.action
                              )}`}
                            >
                              {row.member.label}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            <span
                              className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(
                                row.roster.processable,
                                row.roster.action
                              )}`}
                            >
                              {row.roster.label}
                            </span>
                            {row.roster.warning && (
                              <div className="mt-1 text-xs text-yellow-700">
                                {row.roster.warning}
                              </div>
                            )}
                          </td>
                          {result && (
                            <td className="px-3 py-2">
                              {row.result?.error ? (
                                <span className="text-destructive">{row.result.error}</span>
                              ) : (
                                <span>
                                  {row.result?.member}
                                  {row.result?.roster !== "skipped"
                                    ? ` / roster ${row.result?.roster}`
                                    : ""}
                                  {row.result?.emailStatus === "failed" ? " / email failed" : ""}
                                </span>
                              )}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          <aside className="space-y-4 rounded-md border border-border p-4">
            <div>
              <label className="mb-1 block text-sm font-medium">Default role</label>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={options.defaultRole}
                onChange={(event) => {
                  setOptions({ ...options, defaultRole: event.target.value as ImportRole })
                  resetResults()
                }}
              >
                {roleOptions.map((role) => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium">Member handling</label>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={options.memberImportMode}
                onChange={(event) => {
                  setOptions({
                    ...options,
                    memberImportMode: event.target.value as ImportOptions["memberImportMode"],
                  })
                  resetResults()
                }}
              >
                <option value="invite">Create pending invitations</option>
                <option value="assign">Create users + assign now</option>
              </select>
              <p className="mt-1 text-xs text-secondary">
                Invite sends users through the accept/setup flow. Assign creates team access
                immediately.
              </p>
              {options.memberImportMode === "assign" && (
                <div className="mt-2 rounded-md border border-yellow-300 bg-yellow-50 px-3 py-2 text-xs text-yellow-900">
                  New emails become Playside user records without passwords. Those users can sign in
                  with Google/Microsoft using the same email, but this flow does not send a password
                  setup link.
                </div>
              )}
            </div>

            <label className="flex items-start gap-2 text-sm">
              <Checkbox
                checked={options.sendEmails}
                onCheckedChange={(checked) => {
                  setOptions({ ...options, sendEmails: Boolean(checked) })
                  resetResults()
                }}
              />
              <span>
                <span className="block font-medium">Send emails</span>
                <span className="text-secondary">
                  {options.memberImportMode === "invite"
                    ? "Send invitation emails with an accept/setup link."
                    : "Send informational team-added emails. No password setup link is included."}
                </span>
              </span>
            </label>

            {options.memberImportMode === "invite" && (
              <div className="space-y-3 rounded-md border border-border bg-surface/40 p-3">
                <label className="flex items-start gap-2 text-sm">
                  <Checkbox
                    checked={options.maxInvitationsToCreate !== null}
                    onCheckedChange={(checked) => {
                      setOptions({
                        ...options,
                        maxInvitationsToCreate: checked ? (options.maxInvitationsToCreate ?? 3) : null,
                      })
                      resetResults()
                    }}
                  />
                  <span>
                    <span className="block font-medium">Limit invitation batch</span>
                    <span className="text-secondary">
                      Create only the first eligible invitations. Already invited emails are skipped,
                      so later runs continue with the next eligible rows.
                    </span>
                  </span>
                </label>

                {options.maxInvitationsToCreate !== null && (
                  <label className="block text-sm">
                    <span className="mb-1 block font-medium">Invitations to create</span>
                    <input
                      type="number"
                      min={1}
                      max={500}
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={options.maxInvitationsToCreate}
                      onChange={(event) => {
                        const value = Number(event.target.value)
                        setOptions({
                          ...options,
                          maxInvitationsToCreate: Number.isFinite(value)
                            ? Math.min(500, Math.max(1, Math.trunc(value)))
                            : 3,
                        })
                        resetResults()
                      }}
                    />
                  </label>
                )}

                <label className="flex items-start gap-2 text-sm">
                  <Checkbox
                    checked={options.allowReinvite}
                    onCheckedChange={(checked) => {
                      setOptions({ ...options, allowReinvite: Boolean(checked) })
                      resetResults()
                    }}
                  />
                  <span>
                    <span className="block font-medium">Allow re-invite</span>
                    <span className="text-secondary">
                      Create a new invitation for emails with declined, revoked, or accepted invite
                      history. Pending invites are still skipped.
                    </span>
                  </span>
                </label>
              </div>
            )}

            <label className="flex items-start gap-2 text-sm">
              <Checkbox
                checked={options.createRosterEntries}
                onCheckedChange={(checked) => {
                  setOptions({ ...options, createRosterEntries: Boolean(checked) })
                  resetResults()
                }}
              />
              <span>
                <span className="block font-medium">Create roster entries</span>
                <span className="text-secondary">
                  Requires first name, last name, and jersey. Position is optional.
                </span>
              </span>
            </label>

            {error && (
              <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </div>
            )}

            <div className="space-y-2 pt-2">
              <Button
                type="button"
                className="w-full"
                onClick={handlePreview}
                disabled={importRows.length === 0 || isPreviewing || isProcessing}
              >
                {isPreviewing ? "Previewing..." : "Preview Import"}
              </Button>
              <Button
                type="button"
                className="w-full"
                variant="secondary"
                onClick={handleProcess}
                disabled={
                  !preview ||
                  preview.summary.processableRows === 0 ||
                  limitExceeded ||
                  isProcessing ||
                  isPreviewing ||
                  !!result
                }
              >
                {isProcessing ? "Processing..." : "Process Import"}
              </Button>
              <Button
                type="button"
                className="w-full"
                variant="ghost"
                onClick={clearImport}
                disabled={!hasImportData || isPreviewing || isProcessing}
              >
                Clear Import
              </Button>
            </div>
          </aside>
        </div>
      </DialogContent>
    </Dialog>
  )
}
